import React, { useState, useEffect, useCallback } from "react";
import { X, Star, Phone, Mail, MessageCircle, ExternalLink, Trash2, Edit2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { STAGES, CATEGORIES, CATEGORY_COLORS, STAGE_COLORS, relTime, fmtINR, fmtDate } from "@/lib/leadUtils";

const ACTIVITY_FILTERS = [
  { key: "all", label: "All" },
  { key: "stage_change,category_change", label: "Stage" },
  { key: "quote_change", label: "Money" },
  { key: "note_added,note_edited,note_deleted", label: "Notes" },
  { key: "followup_set,followup_done,followup_count", label: "Follow-ups" },
];

export default function LeadDrawer({ leadId, open, onClose, onChange, onStageOnboardAttempt }) {
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activityFilter, setActivityFilter] = useState("all");
  const [newNote, setNewNote] = useState("");
  const [showSuggested, setShowSuggested] = useState(false);
  const [showDoneChoice, setShowDoneChoice] = useState(false);
  // Same crash-overlay class we fixed on the project-structure 403s — a
  // failed call inside refresh()'s Promise.all used to propagate as an
  // uncaught error and trigger React's red error overlay. Now we capture
  // it into local state and render a graceful retry block in place of
  // the drawer body.
  const [loadError, setLoadError] = useState(null);

  const refresh = useCallback(async () => {
    if (!leadId) return;
    setLoadError(null);
    try {
      const [l, n, a] = await Promise.all([
        api.get(`/leads/${leadId}`),
        api.get(`/leads/${leadId}/notes`),
        api.get(`/leads/${leadId}/activity`, { params: { limit: 30, type: activityFilter === "all" ? undefined : activityFilter } }),
      ]);
      setLead(l.data);
      setNotes(n.data || []);
      setActivity(a.data.items || []);
    } catch (err) {
      // Network blip / 5xx / 4xx — surface a retry affordance instead of
      // crashing. We don't toast because the drawer is already taking the
      // full attention slot; the inline message is enough.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      setLoadError({
        status: status || "network",
        message: detail || err?.message || "Couldn't load this lead.",
      });
    }
  }, [leadId, activityFilter]);

  useEffect(() => {
    if (open && leadId) {
      refresh();
      api.post("/recently-viewed", { entity_type: "lead", entity_id: leadId, name: "" }).catch(() => {});
    }
  }, [open, leadId, refresh]);

  if (!open) return null;

  const patch = async (body, params = {}) => {
    try {
      const { data } = await api.patch(`/leads/${leadId}`, body, { params });
      setLead(data);
      onChange?.();
    } catch (err) {
      const det = err?.response?.data?.detail;
      // Module 6C.4 Layer 2.2 — structured 422 from the quote-propagation
      // refund guard. `det` arrives as an object; we surface the `message`
      // field so the toast reads e.g. "New quote ₹2,50,000 is less than
      // already-collected ₹3,00,000. Void a payment or issue a refund of
      // ₹50,000 first." — actionable, not [object Object].
      const msg = typeof det === "string"
        ? det
        : (det && typeof det === "object" && det.message)
          ? det.message
          : "Update failed";
      toast.error(msg);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    await api.post(`/leads/${leadId}/notes`, { body: newNote.trim() });
    setNewNote("");
    await refresh();
  };

  const setFu = async (days) => {
    await api.post(`/leads/${leadId}/followup/snooze`, null, { params: { days } });
    await refresh();
    onChange?.();
  };

  const markDone = async (responded) => {
    await api.post(`/leads/${leadId}/followup/done`, { lead_responded: responded });
    setShowDoneChoice(false);
    await refresh();
    onChange?.();
    toast.success(responded === true ? "Marked done · responded" : responded === false ? "Marked done · no response" : "Marked done");
  };

  const star = async () => patch({ starred: !lead.starred });

  const suggestFu = async () => {
    const { data } = await api.get(`/leads/${leadId}/followup/suggest`);
    if (data?.suggested_at) {
      await api.post(`/leads/${leadId}/followup/snooze`, null, { params: { date: data.suggested_at } });
      toast.success(`Set to ${data.suggested_at} — ${data.reason}`);
      await refresh();
      onChange?.();
    }
  };

  const addSecondary = () => {
    const list = lead.secondary_contacts || [];
    if (list.length >= 2) { toast("Max 2 secondary contacts"); return; }
    const name = window.prompt("Contact name?");
    if (!name) return;
    const phone = window.prompt("Contact phone?");
    if (!phone) return;
    patch({ secondary_contacts: [...list, { name, phone, relation: "" }] });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      style={{ background: "rgba(247,243,236,.56)", backdropFilter: "blur(2px)" }}
      onClick={onClose}
      data-testid="lead-drawer"
    >
      <div
        className="h-full w-[460px] max-w-full overflow-y-auto fade-up"
        style={{
          background: "#FDFAF4",
          borderLeft: "1px solid #E4DCCD",
          boxShadow: "-8px 0 32px rgba(60,46,20,.1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Graceful failure state — covers the very common race where the
            user clicks a lead just as a backend pod restarts (502) or the
            preview tunnel hiccups. Replaces the entire drawer body with a
            small retry block; the close button and the original behaviour
            stay intact. */}
        {loadError && !lead && (
          <div className="p-10 text-center" data-testid="drawer-load-error">
            <div className="kicker mb-3" style={{ color: "var(--ds-neg)" }}>
              {loadError.status === "network" ? "Couldn't reach the server" : `Couldn't load lead (${loadError.status})`}
            </div>
            <p className="text-sm mb-6 max-w-xs mx-auto" style={{ color: "var(--ds-text-2)" }}>
              {loadError.message}
            </p>
            <button
              onClick={refresh}
              className="px-4 py-2 text-xs tracking-[0.14em] uppercase"
              style={{ background: "var(--ds-ink)", color: "#F4EFE4", borderRadius: 8 }}
              data-testid="drawer-retry"
            >
              Retry
            </button>
            <button
              onClick={onClose}
              className="ml-3 px-4 py-2 text-xs tracking-[0.14em] uppercase"
              style={{ color: "var(--ds-text-2)" }}
              data-testid="drawer-retry-close"
            >
              Close
            </button>
          </div>
        )}
        {!loadError && lead?.category === "Ace" && <div style={{ height: 3, background: "var(--ds-ink)" }} />}
        {!(loadError && !lead) && (
        <>
        {/* Header */}
        <div
          style={{
            padding: "24px 26px 20px",
            borderBottom: "1px solid #EFE8DC",
          }}
        >
          {/* Top row: star + platform pill + close */}
          <div className="flex items-center gap-3">
            <button
              onClick={star}
              data-testid="drawer-star-btn"
              aria-label="Star lead"
              style={{ transition: "transform .15s" }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.1)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
            >
              <Star
                size={18}
                fill={lead?.starred ? "#C8A063" : "transparent"}
                color={lead?.starred ? "#C8A063" : "#CFC4AE"}
                strokeWidth={2}
              />
            </button>
            {lead?.platform && (
              <span
                style={{
                  background: "#F1EDE2",
                  color: "#A39A8B",
                  padding: "5px 10px",
                  borderRadius: 5,
                  fontSize: 10,
                  letterSpacing: "0.2em",
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                {lead.platform}
              </span>
            )}
            <button
              onClick={onClose}
              data-testid="drawer-close"
              aria-label="Close"
              className="ml-auto"
              style={{
                color: "#B0A891",
                width: 32,
                height: 32,
                display: "grid",
                placeItems: "center",
                borderRadius: 8,
                transition: "background .15s, color .15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#EFE8DC";
                e.currentTarget.style.color = "var(--ds-ink)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "#B0A891";
              }}
            >
              <X size={18} />
            </button>
          </div>

          {/* Name */}
          <h2
            data-testid="drawer-lead-name"
            style={{
              fontFamily: "var(--ds-num-font)",
              fontSize: 34,
              color: "var(--ds-ink)",
              lineHeight: 1.1,
              letterSpacing: "-0.01em",
              fontWeight: 400,
              margin: "12px 0 16px",
            }}
          >
            {lead?.full_name || "—"}
          </h2>

          {/* Contact row */}
          <div className="flex flex-wrap items-center gap-2.5">
            {lead?.phone_display && (
              <a
                href={`tel:${lead.phone}`}
                className="inline-flex items-center gap-2"
                style={{
                  background: "#F3ECDE",
                  border: "1px solid #E7DDCB",
                  borderRadius: 8,
                  padding: "9px 13px",
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: "var(--ds-ink)",
                  textDecoration: "none",
                }}
              >
                <Phone size={13} strokeWidth={2} />
                {lead.phone_display}
              </a>
            )}
            {lead?.phone && (
              <a
                href={`https://wa.me/91${lead.phone}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2"
                style={{
                  background: "#25D366",
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 14px",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#fff",
                  textDecoration: "none",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.5 14.4c-.3-.1-1.7-.8-2-1-.3-.1-.5-.2-.6.1-.2.3-.7.9-.8 1.1-.1.2-.3.2-.6.1-.3-.1-1.2-.4-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.4.1-.6l.4-.5c.1-.2.2-.3.3-.5.1-.2 0-.3 0-.5l-.7-1.8c-.2-.5-.4-.4-.5-.4h-.5c-.2 0-.5.1-.7.3-.2.3-.9.9-.9 2.3 0 1.3.9 2.6 1 2.8.1.2 1.9 2.9 4.6 4 .6.3 1.1.4 1.5.6.6.2 1.2.2 1.6.1.5-.1 1.7-.7 1.9-1.3.2-.7.2-1.2.2-1.3-.1-.1-.3-.2-.6-.3zM12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.5 1.3 5L2 22l5.2-1.4c1.5.8 3.1 1.2 4.8 1.2 5.5 0 10-4.5 10-10S17.5 2 12 2zm0 18c-1.6 0-3.1-.4-4.4-1.2l-.3-.2-3.1.8.8-3-.2-.3C3.9 15 3.4 13.6 3.4 12c0-4.7 3.9-8.6 8.6-8.6 4.7 0 8.6 3.9 8.6 8.6 0 4.7-3.9 8-8.6 8z"/>
                </svg>
                WhatsApp
              </a>
            )}
            {lead?.email && (
              <a
                href={`mailto:${lead.email}`}
                className="inline-flex items-center gap-2"
                style={{
                  background: "#fff",
                  border: "1px solid #E4DCCD",
                  borderRadius: 8,
                  padding: "9px 13px",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "var(--ds-text)",
                  textDecoration: "none",
                }}
              >
                <Mail size={13} strokeWidth={2} />
                {lead.email}
              </a>
            )}
            {lead?.inbox_url && (
              <a
                href={lead.inbox_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2"
                style={{
                  background: "#fff",
                  border: "1px solid #E4DCCD",
                  borderRadius: 8,
                  padding: "9px 13px",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "var(--ds-text)",
                  textDecoration: "none",
                }}
              >
                <ExternalLink size={13} strokeWidth={2} />
                Open conversation
              </a>
            )}
            {lead?.project_id && (
              <a
                href={`/projects/${lead.project_id}`}
                data-testid="drawer-view-project"
                className="inline-flex items-center gap-2"
                style={{
                  background: "var(--ds-ink)",
                  color: "#F4EFE4",
                  border: "none",
                  borderRadius: 8,
                  padding: "9px 13px",
                  fontSize: 13,
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                <ExternalLink size={13} strokeWidth={2} />
                View project
              </a>
            )}
          </div>
        </div>

        {/* Lead details — Phase 6F.5: post-create editability. wedding_date
            is nullable: the user may not know it at lead creation and
            wants to fill it in later. Phone normalization + duplicate-
            phone collision (409) handled by the backend. */}
        <Section title="Lead details">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Editor
              label="Full name"
              value={lead?.full_name}
              onSave={(v) => patch({ full_name: v })}
              testId="drawer-full-name"
            />
            <Editor
              label="Phone"
              value={lead?.phone_display || lead?.phone}
              onSave={(v) => patch({ phone: v })}
              hint="10-digit Indian number"
              testId="drawer-phone"
            />
            <Editor
              label="Email"
              value={lead?.email}
              onSave={(v) => patch({ email: v })}
              testId="drawer-email"
            />
            <div>
              <div className="kicker mb-1">EVENT / WEDDING DATE</div>
              <input
                type="date"
                value={lead?.wedding_date || ""}
                onChange={(e) => patch({ wedding_date: e.target.value || null })}
                className="al-input2"
                data-testid="drawer-wedding-date"
              />
              <div className="text-[11px] mt-1 text-brand-muted">
                Optional — leave blank if not set yet.
              </div>
            </div>
            <div className="col-span-2">
              <Editor
                label="Event venue / status"
                value={lead?.venue_status}
                onSave={(v) => patch({ venue_status: v })}
                testId="drawer-venue-status"
              />
            </div>
          </div>
        </Section>

        {/* Secondary contacts */}
        <Section title="Secondary contacts">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(lead?.secondary_contacts || []).map((c, i) => (
              <div
                key={i}
                data-testid={`secondary-contact-${i}`}
                className="flex items-center gap-3"
                style={{
                  background: "#FFF",
                  border: "1px solid #E9E2D6",
                  borderRadius: 9,
                  padding: "12px 14px",
                }}
              >
                {c.relation && (
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: "#4A4439",
                      minWidth: 54,
                    }}
                  >
                    {c.relation}
                  </span>
                )}
                <span style={{ fontWeight: 600, fontSize: 13, color: "var(--ds-ink)" }}>
                  {c.name}
                </span>
                <span style={{ fontWeight: 500, fontSize: 13.5, color: "var(--ds-text-2)" }}>
                  {c.phone_display || c.phone}
                </span>
                <div className="ml-auto flex items-center gap-2">
                  <a
                    href={`tel:${c.phone}`}
                    aria-label="Call"
                    style={{ color: "#A39A8B", padding: 4, borderRadius: 4 }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-ink)")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#A39A8B")}
                  >
                    <Phone size={14} strokeWidth={2} />
                  </a>
                  <a
                    href={`https://wa.me/91${c.phone}`}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="WhatsApp"
                    style={{ color: "#A39A8B", padding: 4, borderRadius: 4 }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-ink)")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#A39A8B")}
                  >
                    <MessageCircle size={14} strokeWidth={2} />
                  </a>
                  <button
                    onClick={() => patch({ secondary_contacts: (lead.secondary_contacts || []).filter((_, j) => j !== i) })}
                    title="Remove"
                    aria-label="Remove"
                    style={{ color: "#A39A8B", padding: 4, borderRadius: 4 }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#A35C45")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#A39A8B")}
                  >
                    <Trash2 size={14} strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}
            {(lead?.secondary_contacts?.length || 0) < 2 && (
              <button
                onClick={addSecondary}
                data-testid="add-secondary-contact"
                className="inline-flex items-center justify-center gap-2"
                style={{
                  width: "100%",
                  border: "1px dashed #D8CCBB",
                  borderRadius: 9,
                  padding: "10px 14px",
                  background: "transparent",
                  color: "var(--ds-text-2)",
                  fontSize: 13,
                  fontWeight: 500,
                  transition: "border-color .15s, color .15s",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#B08D55";
                  e.currentTarget.style.color = "var(--ds-ink)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#D8CCBB";
                  e.currentTarget.style.color = "var(--ds-text-2)";
                }}
              >
                <Plus size={13} /> Add contact
              </button>
            )}
          </div>
          <div className="mt-4 text-sm flex items-center gap-2">
            <span
              style={{
                fontSize: 10.5,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "#A39A8B",
                fontWeight: 600,
              }}
            >
              REFERRED BY
            </span>
            <InlineEditableText value={lead?.referred_by} onSave={(v) => patch({ referred_by: v })} testId="drawer-referred-by" />
          </div>
        </Section>

        {/* Attribution */}
        <Section title="Attribution">
          <div className="grid grid-cols-2" style={{ gap: "18px 24px" }}>
            <KV k="Platform" v={lead?.platform} />
            <KV k="Source" v={lead?.is_organic ? "Organic" : "Paid"} />
            <KV k="Campaign" v={lead?.campaign_name} />
            <KV k="Ad set" v={lead?.adset_name} />
            <KV k="Ad" v={lead?.ad_name} />
            <KV k="Form" v={lead?.form_name} />
          </div>
        </Section>

        {/* Form answers */}
        {lead?.form_answers && Object.keys(lead.form_answers).length > 0 && (
          <Section title="Form answers">
            <div className="space-y-2 text-sm">
              {Object.entries(lead.form_answers).map(([k, v]) => (
                <div key={k}><span className="kicker mr-2">{k.toUpperCase()}</span><span>{v}</span></div>
              ))}
            </div>
          </Section>
        )}

        {/* Tags */}
        <Section title="Tags">
          <div className="flex flex-wrap items-center gap-3">
            <SelectChip
              testId="drawer-category"
              value={lead?.category || ""} options={[{ v: "", l: "—" }, ...CATEGORIES.map((c) => ({ v: c, l: c }))]}
              onChange={(v) => patch({ category: v || null })}
              colors={CATEGORY_COLORS[lead?.category]}
            />
            <SelectChip
              testId="drawer-stage"
              value={lead?.stage} options={STAGES.map((s) => ({ v: s, l: s, disabled: lead?.stage === "Onboarded" && s !== "Onboarded" }))}
              disabledTooltip={lead?.stage === "Onboarded" ? "Use Cancel Project to revert this lead." : ""}
              onChange={(v) => {
                if (v === "Onboarded" && onStageOnboardAttempt) {
                  onStageOnboardAttempt(lead);
                  return;
                }
                patch({ stage: v });
              }}
              colors={{ bg: STAGE_COLORS[lead?.stage], fg: "#fff" }}
            />
            {lead?.suggested_category && !lead?.category && (
              <button
                onClick={() => patch({ category: lead.suggested_category })}
                className="inline-flex items-center gap-1.5 px-3 py-1 text-[11px] tracking-[0.16em] uppercase border-2 border-dashed"
                style={{ borderColor: CATEGORY_COLORS[lead.suggested_category]?.border || "var(--brand-champagne)", color: "var(--brand-ink)", borderRadius: 9999 }}
                data-testid="drawer-apply-suggested"
                title="Apply this suggestion (writes category, logs activity)"
              >
                Suggested: {lead.suggested_category} · Apply →
              </button>
            )}
            {lead?.suggested_category && lead?.category && lead.category !== lead.suggested_category && (
              <button className="text-xs underline text-brand-muted" onClick={() => setShowSuggested((x) => !x)} data-testid="drawer-toggle-why">
                Suggested: {lead.suggested_category}{showSuggested ? " — hide" : " — why?"}
              </button>
            )}
            {showSuggested && (
              <div className="w-full text-xs text-brand-muted mt-1">
                Computed from category rules: budget, venue status, office visit, wedding date proximity.
              </div>
            )}
          </div>
        </Section>

        {/* Fields */}
        <Section title="Fields">
          <div className="space-y-3 text-sm">
            <Editor label="Summary" value={lead?.summary_note} onSave={(v) => patch({ summary_note: v })} multiline testId="drawer-summary-note" />
            <Editor label="Budget band" value={lead?.budget_band} onSave={(v) => patch({ budget_band: v })} testId="drawer-budget-band" />
            <Editor
              label="Quote ₹" value={lead?.quote_value ?? ""} type="number"
              hint={lead?.quote_sent_at ? `Sent ${relTime(lead.quote_sent_at)}` : null}
              onSave={(v) => patch({ quote_value: v === "" ? null : parseInt(v, 10) })}
              testId="drawer-quote-value"
            />
          </div>
        </Section>

        {/* Reminders — Phase 4.3: hidden for Onboarded leads; replaced with project link */}
        {lead?.stage === "Onboarded" && lead?.project_id ? (
          <Section title="Project">
            <a href={`/projects/${lead.project_id}`} data-testid="drawer-project-active-link"
               className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-brand-border hover:border-brand-primary transition-colors"
               style={{ background: "var(--brand-paper-soft)", color: "var(--brand-ink)", borderRadius: 0 }}>
              Project active — View project →
            </a>
          </Section>
        ) : lead?.stage !== "Onboarded" && (
        <Section title="Reminders">
          <div className="grid grid-cols-2 gap-3 text-sm items-end">
            <div>
              <div className="kicker mb-1">NEXT FOLLOW-UP</div>
              <input
                type="date" value={lead?.next_followup_at?.slice(0, 10) || ""}
                onChange={(e) => patch({ next_followup_at: e.target.value || null })}
                className="al-input2" data-testid="drawer-fu-date"
              />
            </div>
            <div>
              <div className="kicker mb-1">FU COUNT</div>
              <div
                data-testid="drawer-fu-count"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase"
                style={{
                  background: (lead?.followup_count || 0) > 0 ? "var(--brand-champagne-bg)" : "var(--brand-paper)",
                  color: (lead?.followup_count || 0) > 0 ? "#7a5a16" : "var(--brand-muted)",
                  border: `1px solid ${(lead?.followup_count || 0) > 0 ? "var(--brand-champagne)" : "var(--brand-border)"}`,
                  borderRadius: 9999,
                }}
                title="Owned by Mark done; not directly editable."
              >
                FU #{lead?.followup_count || 0}
              </div>
              <div className="text-[10px] text-brand-muted mt-1">Use Mark done to bump.</div>
            </div>
            <div className="col-span-2">
              <div className="kicker mb-1">REMINDER NOTE</div>
              <input value={lead?.next_followup_note || ""} onChange={(e) => patch({ next_followup_note: e.target.value })} placeholder="e.g., Send revised quote" className="al-input2" data-testid="drawer-fu-note" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3 items-center">
            {!showDoneChoice ? (
              <button onClick={() => setShowDoneChoice(true)} style={{ background: "var(--ds-pos)", color: "#fff", borderRadius: 8, padding: "9px 14px", fontSize: 12, fontWeight: 600 }} data-testid="drawer-mark-done">Mark done</button>
            ) : (
              <div className="flex items-center gap-2" style={{ background: "#FCFAF5", border: "1px solid #E4DCCD", padding: "7px 11px", borderRadius: 8 }} data-testid="drawer-done-choice">
                <span className="text-xs" style={{ color: "var(--ds-text-2)" }}>Did the lead respond?</span>
                <button onClick={() => markDone(true)} style={{ background: "var(--ds-pos)", color: "#fff", padding: "5px 10px", fontSize: 11, fontWeight: 600, borderRadius: 6 }} data-testid="drawer-done-yes">Yes</button>
                <button onClick={() => markDone(false)} style={{ background: "var(--ds-neg)", color: "#fff", padding: "5px 10px", fontSize: 11, fontWeight: 600, borderRadius: 6 }} data-testid="drawer-done-no">No</button>
                <button onClick={() => markDone(null)} className="ghost-btn" data-testid="drawer-done-skip">Skip</button>
                <button onClick={() => setShowDoneChoice(false)} className="text-xs" style={{ color: "var(--ds-text-2)" }}>×</button>
              </div>
            )}
            <button onClick={() => setFu(1)} className="ghost-btn" data-testid="drawer-snooze-1">+1d</button>
            <button onClick={() => setFu(3)} className="ghost-btn" data-testid="drawer-snooze-3">+3d</button>
            <button onClick={() => setFu(7)} className="ghost-btn">+7d</button>
            <button onClick={suggestFu} className="text-xs underline ml-auto text-brand-muted">Suggest by stage</button>
          </div>
        </Section>
        )}

        {/* Activity */}
        <Section title="Activity">
          <div className="flex gap-2 mb-3 flex-wrap">
            <input
              value={newNote} onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addNote(); }}
              placeholder="Add a note (Enter to save)…" className="flex-1 al-input2" data-testid="drawer-new-note"
            />
            <button onClick={addNote} style={{ background: "var(--ds-ink)", color: "#F4EFE4", padding: "10px 16px", fontSize: 13, fontWeight: 600, borderRadius: 8 }} data-testid="drawer-add-note">Add</button>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {ACTIVITY_FILTERS.filter((f) => !(lead?.stage === "Onboarded" && f.key.startsWith("followup_"))).map((f) => (
              <button
                key={f.key}
                onClick={() => setActivityFilter(f.key)}
                className="px-2.5 py-1 text-[11px] tracking-[0.18em] uppercase border"
                style={{
                  borderColor: activityFilter === f.key ? "var(--brand-primary)" : "var(--brand-border)",
                  color: activityFilter === f.key ? "var(--brand-primary)" : "var(--brand-muted)",
                  background: activityFilter === f.key ? "var(--brand-paper)" : "transparent",
                  borderRadius: 9999,
                }}
                data-testid={`activity-filter-${f.label.toLowerCase()}`}
              >{f.label}</button>
            ))}
          </div>
          <div className="space-y-3" data-testid="activity-list">
            {notes.length === 0 && activity.length === 0 && (
              <p className="text-sm italic text-brand-muted">No activity yet.</p>
            )}
            {notes.map((n) => (
              <NoteRow key={n.id} note={n} leadId={leadId} onChanged={refresh} />
            ))}
            {activity.map((a) => (
              <div key={a.id} className="text-sm" data-testid={`activity-${a.type}`}>
                <span className="inline-block px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase mr-2" style={{ background: "var(--brand-paper)", color: "var(--brand-primary)" }}>{a.type.replace(/_/g, " ")}</span>
                <span className="text-brand-body">
                  {a.from_value !== null && a.from_value !== undefined ? <><span className="text-brand-muted">{String(a.from_value)}</span> → </> : null}
                  <span className="font-medium">{String(a.to_value ?? "")}</span>
                </span>
                <span className="text-brand-muted text-xs ml-2">{a.actor} · {relTime(a.created_at)}</span>
              </div>
            ))}
          </div>
        </Section>

        <style>{`
          /* Phase 7.0 Screen C — drawer field style (.fld). The legacy
             .al-input2 class is aliased to .fld so existing usages adopt
             the new look without per-call rewrites. */
          .al-input2, .fld {
            width: 100%;
            padding: 10px 12px;
            border: 1px solid #E4DCCD;
            background: #FCFAF5;
            color: var(--ds-text);
            font-size: 14px;
            font-family: var(--ds-body-font);
            outline: none;
            border-radius: 8px;
            transition: border-color .15s, box-shadow .15s;
          }
          .al-input2:focus, .fld:focus {
            border-color: #B08D55;
            box-shadow: 0 0 0 3px rgba(176,141,85,.12);
          }
          .ghost-btn {
            padding: 7px 12px;
            font-size: 12px;
            font-weight: 600;
            border: 1px solid #E4DCCD;
            color: var(--ds-ink);
            background: #fff;
            border-radius: 7px;
            transition: border-color .15s;
          }
          .ghost-btn:hover { border-color: #B08D55; }
        `}</style>
        </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  // Phase 7.0 Screen C — sections separated by 1px #EFE8DC dividers,
  // 24px/26px horizontal/vertical padding. Title is uppercase pine 11px/700/0.2em
  // (different from .kicker which is muted) so the section structure reads.
  return (
    <section style={{ padding: "24px 26px", borderTop: "1px solid #EFE8DC" }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: "var(--ds-ink)",
          marginBottom: 14,
        }}
      >
        {title.toUpperCase()}
      </div>
      {children}
    </section>
  );
}

function KV({ k, v }) {
  // Attribution K/V cell — muted label above the value. Empty values
  // render as a muted dash so the grid stays visually anchored.
  return (
    <div>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: "#A39A8B",
          marginBottom: 5,
        }}
      >
        {k}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 500,
          color: v ? "var(--ds-text)" : "#B0A891",
        }}
      >
        {v || "—"}
      </div>
    </div>
  );
}

function ChipLink({ href, icon, label, style }) {
  return (
    <a
      href={href}
      target={href?.startsWith("http") ? "_blank" : undefined}
      rel="noreferrer"
      className="inline-flex items-center gap-1.5"
      style={{
        background: "#fff",
        border: "1px solid #E4DCCD",
        color: "var(--ds-ink)",
        borderRadius: 8,
        padding: "8px 12px",
        fontSize: 13,
        fontWeight: 500,
        textDecoration: "none",
        ...style,
      }}
    >
      {icon}{label}
    </a>
  );
}

function SelectChip({ value, options, onChange, colors, testId, disabledTooltip }) {
  const fg = colors?.fg || "var(--brand-ink)";
  const bg = colors?.bg || "#fff";
  const border = colors?.border || "var(--brand-border)";
  const display = options.find((o) => o.v === value)?.l || "—";
  return (
    <span className="relative inline-block" title={disabledTooltip || ""}>
      <span className="inline-flex items-center px-3 py-1 text-[11px] tracking-[0.16em] uppercase" style={{ background: bg, color: fg, border: `1px solid ${border}`, borderRadius: 9999 }} data-testid={testId}>{display}</span>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" onClick={(e) => e.stopPropagation()} data-testid={`${testId}-select`}>
        {options.map((o) => <option key={o.v} value={o.v} disabled={!!o.disabled}>{o.l}{o.disabled ? " (disabled)" : ""}</option>)}
      </select>
    </span>
  );
}

function InlineEditableText({ value, onSave, testId }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  if (!edit) {
    return <button onClick={() => setEdit(true)} className="text-sm hover:underline" data-testid={testId}>{value || "—"}</button>;
  }
  return (
    <input
      autoFocus value={v} onChange={(e) => setV(e.target.value)}
      onBlur={() => { setEdit(false); if (v !== value) onSave(v); }}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setV(value || ""); setEdit(false); } }}
      className="al-input2 max-w-[260px]"
    />
  );
}

function Editor({ label, value, onSave, type = "text", multiline = false, hint, testId }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  return (
    <div>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: "#A39A8B",
          marginBottom: 6,
        }}
      >
        {label.toUpperCase()}
      </div>
      {multiline ? (
        <textarea value={v} onChange={(e) => setV(e.target.value)} onBlur={() => { if ((v ?? "") !== (value ?? "")) onSave(v); }} rows={3} className="fld" data-testid={testId} />
      ) : (
        <input value={v} type={type} onChange={(e) => setV(e.target.value)} onBlur={() => { if (String(v ?? "") !== String(value ?? "")) onSave(v); }} className="fld" data-testid={testId} />
      )}
      {hint && <div className="text-[11px] mt-1" style={{ color: "var(--ds-text-2)" }}>{hint}</div>}
    </div>
  );
}

function NoteRow({ note, leadId, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(note.body);
  const tooOld = (Date.now() - new Date(note.created_at).getTime()) > 24 * 3600 * 1000;
  const save = async () => {
    try {
      await api.patch(`/leads/${leadId}/notes/${note.id}`, { body });
      setEditing(false);
      onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Edit failed"); }
  };
  const del = async () => {
    if (!window.confirm("Delete note?")) return;
    try {
      await api.delete(`/leads/${leadId}/notes/${note.id}`);
      onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="text-sm group" data-testid={`note-${note.id}`}>
      <div className="flex items-start gap-2">
        <span className="inline-block px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase mr-1" style={{ background: "var(--brand-paper)", color: "var(--brand-primary)" }}>NOTE</span>
        {!editing ? (
          <span className="flex-1 whitespace-pre-wrap">{note.body}</span>
        ) : (
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2} className="flex-1 al-input2" data-testid={`note-${note.id}-edit-input`} />
        )}
        {!tooOld && (
          <div className="opacity-0 group-hover:opacity-100 flex gap-1">
            {!editing ? (
              <>
                <button onClick={() => setEditing(true)} data-testid={`note-${note.id}-edit`}><Edit2 size={12} /></button>
                <button onClick={del} data-testid={`note-${note.id}-delete`}><Trash2 size={12} color="var(--brand-danger)" /></button>
              </>
            ) : (
              <>
                <button onClick={save} className="text-xs underline" data-testid={`note-${note.id}-save`}>Save</button>
                <button onClick={() => { setEditing(false); setBody(note.body); }} className="text-xs underline">Cancel</button>
              </>
            )}
          </div>
        )}
      </div>
      <div className="text-brand-muted text-xs mt-1">{note.author} · {relTime(note.created_at)}{note.edited_at && " · edited"}</div>
    </div>
  );
}
